// Copyright 2026 AllSpice. All rights reserved.

import {SCHEMA_TYPES, type SchemaType} from '../../features/allspicejson.ts';

// Filter paths and grouped filter item IDs share this delimiter so the filter
// tree, override persistence, and path normalization all speak the same format.
export const ATTRIBUTES_DELIMITER = '|x42~|';

// Top-level category fields returned by Crysknife for each schema type.
// Paths that already start with one of these are already normalized.
export const SCHEMATIC_FIELDS = ['components', 'junctions', 'no_connects', 'ports', 'sheet_refs', 'wires'] as const;
export const PCB_FIELDS = ['arcs', 'pads', 'regions', 'tracks', 'texts', 'vias'] as const;
const KNOWN_CATEGORY_FIELDS = new Set<string>([
  ...SCHEMATIC_FIELDS,
  'graphics',
  'component_instances',
  ...PCB_FIELDS,
  'padstacks',
]);

export function makeDiffFilterTargetId(pageId: string | null | undefined, dataId: string): string {
  if (!pageId) {
    return dataId;
  }

  return `${pageId}${ATTRIBUTES_DELIMITER}${dataId}`;
}

export function parseDiffFilterTargetId(targetId: string): {pageId: string | null; dataId: string} {
  const delimiterIndex = targetId.indexOf(ATTRIBUTES_DELIMITER);
  if (delimiterIndex === -1) {
    return {
      pageId: null,
      dataId: targetId,
    };
  }

  return {
    pageId: targetId.substring(0, delimiterIndex),
    dataId: targetId.substring(delimiterIndex + ATTRIBUTES_DELIMITER.length),
  };
}

export function normalizeFilterPath(path: string): string {
  const delimiterIndex = path.indexOf(ATTRIBUTES_DELIMITER);
  const firstSegment = delimiterIndex === -1 ? path : path.substring(0, delimiterIndex);

  if (KNOWN_CATEGORY_FIELDS.has(firstSegment)) return path;
  if (delimiterIndex === -1) return '';

  return path.substring(delimiterIndex + ATTRIBUTES_DELIMITER.length);
}

export function determineSchemaType(
  headContents: Record<string, unknown> | null | undefined,
  baseContents: Record<string, unknown> | null | undefined,
): SchemaType {
  const schemaType = headContents?.['type'] ?? baseContents?.['type'];
  return typeof schemaType === 'string' ? schemaType as SchemaType : SCHEMA_TYPES.UNKNOWN;
}
